import { useMemo, useState } from "react";
import { useLocale } from "@acto/web-common/locales";
import { buildCalendarMonth } from "./buildCalendarMonth";
import { buildCalendarWeek } from "./buildCalendarWeek";
import {
  addDaysUtc,
  addMonthsUtc,
  createCalendarFormat,
  startOfDayUtc,
  startOfMonthUtc,
  startOfWeekUtc,
  todayUtc,
  utcDate,
} from "./dateUtils";
import type { CalendarCell, CalendarDayModel } from "./types";

export interface CalendarController {
  /** Localized label for the visible period (e.g. "Juni 2026" or "1 - 7. Juni"). */
  title: string;
  weekdayLabels: string[];
  weeks: CalendarDayModel[][];
  selectedDate: Date | null;
  /** The month currently in view (month grids only). Lets a month-picker reflect/jump the view. */
  visibleMonth?: Date;
  goToPrevious: () => void;
  goToNext: () => void;
  /** Jump to [monthIndex] (0–11) within the year of the currently-visible month. Month grids only. */
  goToMonth?: (monthIndex: number) => void;
  /** Move the visible period to contain [date] and select it. Used to sync one controller to another
   * (e.g. carrying the selection across a week↔month view toggle). */
  goToDate: (date: Date) => void;
  selectDay: (date: Date) => void;
}

interface CalendarOptions {
  /** Day the calendar opens on. Defaults to the selected day, else today. */
  initialDate?: Date;
  /** When false, weekends (Sa/So) are hidden — a Mo–Fr grid. Defaults to true (full week). */
  includeWeekends?: boolean;
  /** Earliest selectable day (inclusive); earlier days render disabled. */
  minDate?: Date;
  /** Latest selectable day (inclusive); later days render disabled. */
  maxDate?: Date;
  /** Controlled selected day. When set, the hook reflects it and reports changes via onSelectDate. */
  selectedDate?: Date | null;
  /** Uncontrolled initial selection (e.g. todayUtc()). Ignored when selectedDate is provided. */
  defaultSelectedDate?: Date | null;
  /** Notified when a selectable day is chosen. */
  onSelectDate?: (date: Date) => void;
}

interface DayPolicy {
  selectedTime: number | null;
  minTime: number | null;
  maxTime: number | null;
}

const toDay = (date: Date | null | undefined): Date | null => (date ? startOfDayUtc(date) : null);

// Shift a selected day by whole months, preserving day-of-month but clamping to the target month's
// length (31 Jan → 28 Feb, not a spill into March). `addMonthsUtc` only shifts to the 1st, so it
// can't carry a selection's day.
const shiftSelectedMonth = (date: Date, delta: number): Date => {
  const firstOfTarget = addMonthsUtc(date, delta);
  const lastDay = utcDate(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth() + 1, 0).getUTCDate();
  return utcDate(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth(), Math.min(date.getUTCDate(), lastDay));
};

function toModel(cell: CalendarCell, policy: DayPolicy, outOfScope: boolean): CalendarDayModel {
  const time = cell.date.getTime();
  const disabled =
    outOfScope ||
    (policy.minTime !== null && time < policy.minTime) ||
    (policy.maxTime !== null && time > policy.maxTime);

  return {
    date: cell.date,
    label: cell.label,
    ariaLabel: cell.ariaLabel,
    state: disabled ? "disabled" : policy.selectedTime === time ? "selected" : "default",
  };
}

function useDayPolicy(selected: Date | null, minDate?: Date, maxDate?: Date): DayPolicy {
  const selectedTime = selected ? selected.getTime() : null;
  const minTime = minDate ? startOfDayUtc(minDate).getTime() : null;
  const maxTime = maxDate ? startOfDayUtc(maxDate).getTime() : null;

  return { selectedTime, minTime, maxTime };
}

function useSelection({ selectedDate, defaultSelectedDate, onSelectDate }: CalendarOptions): {
  selected: Date | null;
  commit: (day: Date) => void;
  /** True when the parent owns the selection (a `selectedDate` prop was passed). */
  controlled: boolean;
} {
  const controlled = selectedDate !== undefined;
  const [internal, setInternal] = useState<Date | null>(() => toDay(defaultSelectedDate));
  const selected = controlled ? toDay(selectedDate) : internal;

  return {
    selected,
    commit: (day) => {
      if (!controlled) setInternal(day);
      onSelectDate?.(day);
    },
    controlled,
  };
}

const isSelectable = (time: number, policy: DayPolicy): boolean =>
  (policy.minTime === null || time >= policy.minTime) && (policy.maxTime === null || time <= policy.maxTime);

const makeSelectDay =
  (policy: DayPolicy, commit: (day: Date) => void) =>
  (date: Date): void => {
    const day = startOfDayUtc(date);
    if (isSelectable(day.getTime(), policy)) commit(day);
  };

export function useCalendarMonth(options: CalendarOptions = {}): CalendarController {
  const { locale } = useLocale();
  const format = useMemo(() => createCalendarFormat(locale), [locale]);
  const { selected, commit, controlled } = useSelection(options);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    startOfMonthUtc(options.initialDate ?? selected ?? todayUtc()),
  );

  const includeWeekends = options.includeWeekends ?? true;
  const policy = useDayPolicy(selected, options.minDate, options.maxDate);
  const monthIndex = visibleMonth.getUTCMonth();
  // Monday-first weeks → weekdays are the first five columns; drop Sa/So when weekends are excluded.
  const { weekdayLabels, weeks } = useMemo(() => {
    const built = buildCalendarMonth(visibleMonth, format);
    if (includeWeekends) return built;
    return {
      weekdayLabels: built.weekdayLabels.slice(0, 5),
      weeks: built.weeks.map((week) => week.slice(0, 5)),
    };
  }, [visibleMonth, format, includeWeekends]);

  const modelWeeks = weeks.map((week) =>
    week.map((cell) => toModel(cell, policy, cell.date.getUTCMonth() !== monthIndex)),
  );

  // Paging months carries the selection by the same offset, so the visible grid keeps a highlighted
  // day and any list keyed off `selectedDate` reflects a day in view (mirrors `shiftWeek`). Skipped
  // when controlled: the parent owns the selection, so committing here would fire onSelectDate and
  // move the caller's date just from browsing (e.g. DatePicker paging months).
  const shiftMonth = (delta: number) => {
    setVisibleMonth((month) => addMonthsUtc(month, delta));
    if (selected && !controlled) commit(shiftSelectedMonth(selected, delta));
  };

  return {
    title: format.monthTitle(visibleMonth),
    weekdayLabels,
    weeks: modelWeeks,
    selectedDate: selected,
    visibleMonth,
    goToPrevious: () => shiftMonth(-1),
    goToNext: () => shiftMonth(1),
    goToMonth: (target) => setVisibleMonth((month) => utcDate(month.getUTCFullYear(), target, 1)),
    goToDate: (date) => {
      setVisibleMonth(startOfMonthUtc(date));
      commit(startOfDayUtc(date));
    },
    selectDay: makeSelectDay(policy, commit),
  };
}

export function useCalendarWeek(options: CalendarOptions = {}): CalendarController {
  const { locale } = useLocale();
  const format = useMemo(() => createCalendarFormat(locale), [locale]);
  const { selected, commit, controlled } = useSelection(options);
  const [weekStart, setWeekStart] = useState(() => startOfWeekUtc(options.initialDate ?? selected ?? todayUtc()));

  const includeWeekends = options.includeWeekends ?? true;
  const policy = useDayPolicy(selected, options.minDate, options.maxDate);
  // Monday-first weeks → the first five days are Mo–Fr; drop Sa/So when weekends are excluded.
  const { weekdayLabels, days } = useMemo(() => {
    const built = buildCalendarWeek(weekStart, format);
    if (includeWeekends) return built;
    return { weekdayLabels: built.weekdayLabels.slice(0, 5), days: built.days.slice(0, 5) };
  }, [weekStart, format, includeWeekends]);

  const modelDays = days.map((cell) => toModel(cell, policy, false));

  // Paging weeks carries the selection to the same weekday in the new week, so the visible grid
  // always has a highlighted day and any list keyed off `selectedDate` reflects a day in view.
  // Without this the selection strands on the old week (no highlight, stale list) after every nav.
  // Skipped when controlled: the parent owns the selection (committing would fire onSelectDate).
  const shiftWeek = (deltaDays: number) => {
    setWeekStart((week) => addDaysUtc(week, deltaDays));
    if (selected && !controlled) commit(addDaysUtc(selected, deltaDays));
  };

  return {
    title: format.weekRange(weekStart, addDaysUtc(weekStart, 6)),
    weekdayLabels,
    weeks: [modelDays],
    selectedDate: selected,
    goToPrevious: () => shiftWeek(-7),
    goToNext: () => shiftWeek(7),
    goToDate: (date) => {
      setWeekStart(startOfWeekUtc(date));
      commit(startOfDayUtc(date));
    },
    selectDay: makeSelectDay(policy, commit),
  };
}
